/**
 * Operasyon simplified reservation tracking view.
 *
 * The default Excel-grid on AcenteReservationTracking.jsx is fine for admin
 * — they need every column visible. Operasyon's job on this page is very
 * different: they scan the incoming pipeline, spot anomalies, and either
 * (a) request the agent to double-check ("İnceleme Talep Et") or (b) let it
 * pass on to Fiyat Onayları.
 *
 * So this component renders one BIG horizontal row per reservation:
 *   [ Big Agency Name             ]
 *   [ small group name                                        ]
 *   Giriş • Toplam Umreci • Toplam Oda            [Detay] [İnceleme Talep Et]
 *
 * No reservation numbers, no per-capacity room split, no columns to
 * decipher. Detail button opens the standard reservation detail page in a
 * new tab; İnceleme Talep Et fires a modal to enter a reason and POSTs
 * /reservations/{id}/request-review.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search, Users, BedDouble, Calendar, ExternalLink, AlertTriangle,
  RefreshCw, CheckCircle2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";




export default function AcenteReservationTrackingOperasyon() {
  const [rows, setRows] = useState([]);
  const [agencies, setAgencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reviewTarget, setReviewTarget] = useState(null); // { row, reason, busy }

  const load = useCallback(async () => {
    try {
      const [rezR, agR] = await Promise.all([
        panelApi.get("/reservations"),
        panelApi.get("/agencies").catch(() => ({ data: [] })),
      ]);
      setRows(rezR.data || []);
      const m = {}; (agR.data || []).forEach((a) => { m[a.id] = a.name; });
      setAgencies(m);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rezervasyonlar alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let out = [...rows];
    const s = q.trim().toLowerCase();
    if (s) {
      out = out.filter((r) =>
        (r.group_name || "").toLowerCase().includes(s) ||
        (agencies[r.agency_id] || "").toLowerCase().includes(s) ||
        (r.hotel_name || "").toLowerCase().includes(s) ||
        (r.customer_phone || "").toLowerCase().includes(s)
      );
    }
    out.sort((a, b) => (a.checkin || "").localeCompare(b.checkin || ""));
    return out;
  }, [rows, q, agencies]);

  const openReview = (row) => setReviewTarget({ row, reason: "", busy: false });

  const submitReview = async () => {
    if (!reviewTarget) return;
    const reason = (reviewTarget.reason || "").trim();
    setReviewTarget({ ...reviewTarget, busy: true });
    try {
      await panelApi.post(`/reservations/${reviewTarget.row.id}/request-review`, {
        reason: reason || "",
      });
      toast.success("İnceleme talebi iletildi — rezervasyon 'İncelemede' olarak işaretlendi");
      // Reflect the new status locally so the row updates without a full reload.
      setRows((prev) => prev.map((r) => r.id === reviewTarget.row.id
        ? { ...r, status: "incelemede", review_reason: reason }
        : r
      ));
      setReviewTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talep gönderilemedi");
      setReviewTarget((s) => (s ? { ...s, busy: false } : s));
    }
  };

  return (
    <div className="space-y-5" data-testid="acente-tracking-operasyon">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-brand-orange font-semibold">Operasyon · Takip</div>
          <h1 className="font-serif text-3xl text-brand-navy mt-1">Rezervasyon Takip</h1>
          <p className="text-xs text-slate-500 mt-1">
            Gelen rezervasyonları sırayla inceleyin. Hata gördüğünüzde acenteden düzeltme isteyin.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-brand-navy/10 px-4 py-3 text-sm flex items-center gap-4" data-testid="tracking-ops-stats">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-brand-navy/10 text-brand-navy flex items-center justify-center">
              <Calendar size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Toplam</div>
              <div className="font-serif text-2xl leading-none mt-0.5" data-testid="tracking-ops-count">{filtered.length}</div>
            </div>
          </div>
          <button
            onClick={load}
            className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1"
            data-testid="tracking-ops-refresh"
            title="Listeyi yenile"
          >
            <RefreshCw size={12} /> Yenile
          </button>
        </div>
      </header>

      <div className="bg-white rounded-2xl border border-brand-navy/10 p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Acente / grup / otel / telefon ara..."
            className="pl-8"
            data-testid="tracking-ops-search"
          />
        </div>
      </div>

      <div className="space-y-2" data-testid="tracking-ops-list">
        {loading && (
          <div className="rounded-2xl bg-white border border-brand-navy/10 p-8 text-center text-slate-500">Yükleniyor...</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-white border border-brand-navy/10 p-10 text-center text-slate-500">
            <div className="inline-flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center">
                <CheckCircle2 size={22} />
              </div>
              <div className="text-sm">Bu kriterlere uyan rezervasyon yok.</div>
            </div>
          </div>
        )}
        {filtered.map((r) => {
          const heads = (r.adults || 0) + (r.children || 0) + (r.staff || 0);
          const rooms = ["r1","r2","r3","r4","r5"].reduce((s, k) => s + (r.rooms?.[k] || 0), 0);
          const agencyName = agencies[r.agency_id] || "Bağımsız";
          return (
            <div
              key={r.id}
              className="rounded-2xl bg-white border border-brand-navy/10 hover:border-brand-orange/40 hover:shadow-sm transition p-4 sm:p-5 flex items-center gap-4 flex-wrap"
              data-testid={`tracking-ops-row-${r.id}`}
            >
              <div className="flex-1 min-w-[240px]">
                {/* Agency name — deliberately bigger so operasyon can scan
                    the pipeline by company without reading further. */}
                <div className="font-serif text-2xl text-brand-navy leading-tight" data-testid={`tracking-ops-agency-${r.id}`}>
                  {agencyName}
                </div>
                {/* Group name — smaller, secondary. */}
                <div className="text-sm text-slate-500 mt-0.5" data-testid={`tracking-ops-group-${r.id}`}>
                  {r.group_name || <span className="italic">Grup adı yok</span>}
                  {r.customer_phone && <span className="ml-2 text-[11px]">📞 {r.customer_phone}</span>}
                </div>
              </div>

              <div className="flex items-center gap-4 flex-wrap">
                <Stat
                  icon={<Calendar size={14} />}
                  label="Giriş"
                  value={formatTr(r.checkin)}
                  sub={`${r.nights || 0} gece`}
                  testid={`tracking-ops-date-${r.id}`}
                />
                <Stat
                  icon={<Users size={14} />}
                  label="Toplam Umreci"
                  value={heads}
                  testid={`tracking-ops-people-${r.id}`}
                />
                <Stat
                  icon={<BedDouble size={14} />}
                  label="Toplam Oda"
                  value={rooms}
                  testid={`tracking-ops-rooms-${r.id}`}
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap justify-end">
                {r.is_revised && (
                  <span
                    className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 border bg-amber-100 text-amber-800 border-amber-200"
                    data-testid={`tracking-ops-revised-${r.id}`}
                    title={r.revised_at ? `Revize: ${new Date(r.revised_at).toLocaleString("tr-TR")}` : "Revize edildi"}
                  >
                    ✎ Revize
                  </span>
                )}
                <Link
                  to={`/acente/rezervasyon/${r.id}`}
                  className="text-xs px-3 py-1.5 rounded-full bg-brand-navy/5 hover:bg-brand-navy/10 text-brand-navy inline-flex items-center gap-1"
                  data-testid={`tracking-ops-detail-${r.id}`}
                  title="Detayları göster"
                >
                  <ExternalLink size={11}/> Detay
                </Link>
                <button
                  onClick={() => openReview(r)}
                  className="text-xs px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white inline-flex items-center gap-1"
                  data-testid={`tracking-ops-request-review-${r.id}`}
                  title="Bu rezervasyonda bir hata görüyorum"
                >
                  <AlertTriangle size={11}/> İnceleme Talep Et
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!reviewTarget} onOpenChange={(v) => !v && setReviewTarget(null)}>
        <AlertDialogContent data-testid="tracking-ops-review-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>İnceleme Talep Et</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{reviewTarget?.row ? (agencies[reviewTarget.row.agency_id] || "Rezervasyon") : ""}</b>
              {" "}rezervasyonu <b>İncelemede</b> olarak işaretlenecek. İsterseniz kısa bir sebep de ekleyin — acente bunu görecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reviewTarget && (
            <div>
              <Label>Sebep (opsiyonel)</Label>
              <Input
                value={reviewTarget.reason}
                onChange={(e) => setReviewTarget((s) => ({ ...s, reason: e.target.value }))}
                placeholder="Örn. Oda sayısı ile umreci sayısı uyuşmuyor gibi görünüyor."
                data-testid="tracking-ops-review-reason"
                autoFocus
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tracking-ops-review-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitReview}
              disabled={!!reviewTarget?.busy}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              data-testid="tracking-ops-review-confirm"
            >
              {reviewTarget?.busy ? "Gönderiliyor..." : "İnceleme Talep Et"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl bg-canvas-cream/50 border border-brand-navy/5 px-3 py-2 flex items-center gap-2 min-w-[110px]">
      <div className="w-8 h-8 rounded-lg bg-white text-brand-orange flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-serif text-lg text-brand-navy leading-none mt-0.5" data-testid={testid}>{value}</div>
        {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
